<template>
  <form name="form" v-on:submit.prevent="submit" v-on:change="changeHandler">
    <fieldset
      :disabled="readonly"
      style="border:0;padding:0;margin:0;min-width:0;"
    >
      <div class="card-body">
        <div class="float-right" v-if="!readonly">
          <form-button :indicator="form.processing()" v-on:clicked="submit"
            >Save
          </form-button>
        </div>
        <div class="clearfix"></div>
        <hr />

        <div class="row">
          <div class="col-lg-6">
            <selectlist
              :options="titles"
              v-model="form.title_id"
              label="Title"
              name="title"
              placeholder="Please Select"
              :show-error="form.errors.has('title')"
              helper="Please select the clients title"
              :error-message="form.errors.get('title')"
            ></selectlist>

            <inputbox
              :value="form.first_name"
              @input="setFirstName"
              name="first_name"
              label="First Name"
              :error-message="form.errors.get('first_name')"
              helper="Please enter the first name of the client"
              :show-error="form.errors.has('first_name')"
            ></inputbox>

            <inputbox
              :value="form.last_name"
              @input="setLastName"
              name="last_name"
              label="Last Name"
              :error-message="form.errors.get('last_name')"
              helper="Please enter the last name of the client"
              :show-error="form.errors.has('last_name')"
            ></inputbox>

            <div class="col-12">
              <div
                class="alert alert-danger"
                v-if="duplicateNameIncidents.length"
              >
                <span v-if="duplicateNameIncidents.length === 1"
                  >A claim already exists for</span
                >
                <span v-else>Multiple claims already exist with</span>
                a client with that name, this claim could already be set up
                <br />
                <div class="text-center">
                  <button
                    type="button"
                    class="btn btn-danger btn-xs"
                    @click="clearDuplicateNameIncidents"
                  >
                    Dismiss
                  </button>
                </div>

                <table class="table table-borderless">
                  <thead>
                    <tr>
                      <th>Reference</th>
                      <th>Full Name</th>
                      <th>Claim Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(duplicateIncident, i) in duplicateNameIncidents"
                      :key="i"
                    >
                      <td>
                        <a :href="duplicateIncident.url" target="_blank">
                          {{ duplicateIncident.reference }}
                        </a>
                      </td>
                      <td>{{ duplicateIncident.fullName }}</td>
                      <td>
                        {{
                          duplicateIncident.createdAt | date('DD/MM/YYYY HH:mm')
                        }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div class="clearfix"></div>

            <selectlist
              :options="genders"
              v-model="form.gender_id"
              label="Gender"
              name="gender"
              helper="Please enter the gender of the client"
              :show-error="form.errors.has('gender_id')"
              placeholder="Please Select"
              :error-message="form.errors.get('gender_id')"
            ></selectlist>

            <date-of-birth
              name="date_of_birth"
              v-model="form.date_of_birth"
              helper="Please enter the clients date of birth"
              :show-error="form.errors.has('date_of_birth')"
            ></date-of-birth>
          </div>

          <div class="col-lg-6">
            <inputbox
              v-model="form.occupation"
              name="occupation"
              label="Occupation"
              helper="Please enter the occupation of the client"
              :error-message="form.errors.get('occupation')"
              :show-error="form.errors.has('occupation')"
              v-on:input="changeHandler"
            ></inputbox>

            <inputbox
              v-model="form.national_insurance"
              name="national_insurance"
              label="National Insurance"
              helper="Please enter the national insurance number of the client"
            ></inputbox>

            <selectlist
              :options="securityQuestions"
              v-model="form.security_question_id"
              helper="Please choose a security question for the client"
              label="Security Question"
              name="security_question"
              placeholder="Please Select"
              :show-error="form.errors.has('security_question_id')"
            ></selectlist>

            <inputbox
              v-model="form.security_question_answer"
              name="security_question_answer"
              label="Security Question Answer"
              helper="Please enter the answer for the clients security question"
              :show-error="form.errors.has('security_question_answer')"
            ></inputbox>
          </div>
        </div>

        <div class="row">
          <div class="col-lg-6">
            <fieldset>
              <legend>
                Contact Details
                <small class="text-muted"
                  >- Enter all of the clients contact details here</small
                >
              </legend>

              <inputbox
                v-model="form.email_address"
                name="email_address"
                label="Email Address"
                helper="Please enter the email address for the client"
                :show-error="form.errors.has('email_address')"
              ></inputbox>

              <div class="row">
                <div class="col-md-8 offset-md-4">
                  <div class="alert alert-info">
                    <p class="mb-0">
                      The client only needs to provide one contact number.
                    </p>
                  </div>
                </div>
              </div>

              <inputbox
                v-model="form.home_telephone"
                name="home_telephone"
                helper="Please enter the home telephone number for the client"
                label="Home Telephone"
              ></inputbox>

              <inputbox
                v-model="form.work_telephone"
                name="work_telephone"
                helper="Please enter the work telephone number for the client"
                label="Work Telephone"
              ></inputbox>

              <inputbox
                v-model="form.mobile_telephone"
                name="mobile_telephone"
                helper="Please enter the mobile telephone number for the client"
                label="Mobile Telephone"
              ></inputbox>

              <selectlist
                :options="contactMethods"
                v-model="form.contact_method_id"
                helper="Please enter the clients preferred contact method"
                label="Preferred Contact Method"
                name="contact_method"
                placeholder="Please Select"
                :show-error="form.errors.has('contact_method_id')"
              ></selectlist>

              <selectlist
                :options="contactTimes"
                v-model="form.contact_time_id"
                helper="Please enter the clients preferred contact time"
                label="Preferred Contact Time"
                name="contact_time"
                placeholder="Please Select"
                :show-error="form.errors.has('contact_time_id')"
              ></selectlist>
            </fieldset>

            <fieldset>
              <legend>
                Address Details
                <small class="text-muted">
                  - Enter the clients address details
                </small>
              </legend>
              <address-lookup
                :address="address"
                v-on:address="setAddress"
              ></address-lookup>
            </fieldset>
          </div>

          <div class="col-lg-6">
            <fieldset>
              <legend>
                First Contact
                <small class="text-muted">
                  - Enter the details of the client's first contact
                </small>
              </legend>

              <div
                class="form-group"
                :class="{
                  'has-error': form.errors.has('client_first_contact')
                }"
              >
                <div class="col-md-4">
                  <label class="control-label"
                    >Client First Contact Date:</label
                  >
                  <p class="help-block">
                    Please enter the date the client was first contacted
                  </p>
                </div>

                <div class="col-lg-8">
                  <datepicker
                    name="client_first_contact"
                    placeholder="dd/mm/yyyy"
                    input-class="form-control"
                    format="dd/MM/yyyy"
                    v-bind:value="form.client_first_contact"
                    v-on:selected="setClientFirstContact"
                    v-on:input="changeHandler"
                  ></datepicker>
                  <span
                    class="text-danger"
                    v-text="form.errors.get('client_first_contact')"
                    v-if="form.errors.has('client_first_contact')"
                  ></span>
                </div>
              </div>
            </fieldset>

            <fieldset>
              <legend>
                Authorised Contact
                <small class="text-muted">
                  - If the client requires an authorised contact please enter
                  the details below
                </small>
              </legend>
              <toggleswitch
                :labels="true"
                name="authorised_contact_required"
                label="Authorised contact required"
                v-model="form.authorised_contact_required"
              ></toggleswitch>

              <inputbox
                v-model="form.authorised_contact_relation_to_client"
                name="authorised_contact_relation_to_client"
                helper="Relation to client"
                label="Relation to client"
                v-if="form.authorised_contact_required"
              ></inputbox>

              <inputbox
                v-model="form.authorised_contact_name"
                name="authorised_contact_name"
                helper="Please enter the name"
                label="Name"
                v-if="form.authorised_contact_required"
              ></inputbox>

              <inputbox
                v-model="form.authorised_contact_email"
                name="authorised_contact_email"
                helper="Please enter the email"
                label="Email"
                v-if="form.authorised_contact_required"
              ></inputbox>

              <inputbox
                v-model="form.authorised_contact_phone_number"
                name="authorised_contact_phone_number"
                helper="Please enter the phone number"
                label="Phone Number"
                v-if="form.authorised_contact_required"
              ></inputbox>
            </fieldset>

            <fieldset>
              <legend>
                Company Details
                <small class="text-muted"
                  >- If hiring for a company add their details here</small
                >
              </legend>

              <inputbox
                v-model="form.company_name"
                name="company_name"
                helper="Please enter the company name"
                label="Company Name"
              ></inputbox>

              <toggleswitch
                :labels="true"
                name="vat_registered"
                label="Is the company VAT registered?"
                v-model="form.vat_registered"
              ></toggleswitch>

              <inputbox
                v-model="form.vat_number"
                name="vat_number"
                helper="Please enter the VAT Number"
                label="VAT Number"
                v-if="form.vat_registered"
              ></inputbox>
            </fieldset>
          </div>
        </div>

        <fieldset>
          <legend>
            Policy Validation
            <small class="text-muted"
              >- Enter the details of the client's convictions, illnesses and
              claims
            </small>
          </legend>

          <div class="row">
            <div class="col-lg-6">
              <dynamic-inputs
                v-model="form.motoring_convictions"
                name="motoring_convictions"
                key-name="id"
                value-name="body"
                nicename="Motoring conviction"
                placeholder="Please Select"
                label="Motoring convictions within the last 3 years"
                item="Conviction"
                helper="Does the client have any motoring convictions within the last three years? If so, please enter them here"
              ></dynamic-inputs>

              <input-block
                v-model="form.prior_claims"
                name="prior_claims"
                helper="Has the client made any motor insurance claims within the last five years? If so, please enter them here"
                label="Claims within the last 5 years"
              ></input-block>
            </div>

            <div class="col-lg-6">
              <dynamic-inputs
                v-model="form.criminal_convictions"
                name="criminal_convictions"
                key-name="id"
                value-name="body"
                nicename="Criminal conviction"
                placeholder="Please Select"
                label="Number of criminal convictions"
                item="Conviction"
                helper="Please give the number of criminal convictions, if any"
              ></dynamic-inputs>

              <input-block
                v-model="form.illnesses_disabilities"
                name="illnesses_disabilities"
                helper="Does the client have any illnesses or disabilities that are reportable to the DVLA? If so, please enter them here"
                label="Ilnesses or disabilities reportable to the DVLA"
              ></input-block>
            </div>
          </div>
        </fieldset>
      </div>
      <div class="card-footer text-right" v-if="!readonly">
        <form-button :indicator="form.processing()" v-on:clicked="submit"
          >Save
        </form-button>
      </div>
    </fieldset>
  </form>
</template>
<script>
import DynamicInputs from './../../../Generic/DynamicInputs.vue';
import Form from './../../../../Classes/Form';
import debounce from 'lodash/debounce';

export default {
  props: {
    incident: {
      type: String,
      required: true
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },

  components: {
    'dynamic-inputs': DynamicInputs
  },

  data() {
    return {
      form: new Form({
        title_id: '',
        first_name: null,
        last_name: null,
        gender_id: '',
        date_of_birth: window.DateManager.driverStartingDate(),
        occupation: null,
        national_insurance: null,
        security_question_id: '',
        security_question_answer: null,
        company_name: null,
        vat_registered: false,
        vat_number: null,
        email_address: null,
        home_telephone: null,
        work_telephone: null,
        mobile_telephone: null,
        contact_method_id: null,
        contact_time_id: null,
        address_line_one: null,
        address_line_two: null,
        city: null,
        postcode: null,
        criminal_convictions: [],
        motoring_convictions: [],
        prior_claims: null,
        illnesses_disabilities: null,
        client_first_contact: null,
        authorised_contact_required: false,
        authorised_contact_relation_to_client: null,
        authorised_contact_name: null,
        authorised_contact_email: null,
        authorised_contact_phone_number: null
      }),
      client_exists: false,
      data_entered: false,
      duplicateNameIncidents: []
    };
  },

  mounted() {
    this.mount();
  },

  computed: {
    address() {
      return {
        address_line_one: this.form.address_line_one,
        address_line_two: this.form.address_line_two,
        city: this.form.city,
        postcode: this.form.postcode
      };
    },

    titles() {
      return this.$store.getters['Titles/all'];
    },

    genders() {
      return this.$store.getters['Genders/all'];
    },

    securityQuestions() {
      return this.$store.getters['SecurityQuestions/all'];
    },

    contactMethods() {
      return this.$store.getters['ContactMethods/all'];
    },

    contactTimes() {
      return this.$store.getters['ContactTimes/all'];
    },

    client() {
      return this.$store.getters['Incident/client'];
    }
  },

  methods: {
    mount() {
      if (Object.keys(this.client).length) {
        this.setClientDetails(this.client);
      }
    },

    setClientDetails(details) {
      this.client_exists = true;
      this.form.title_id = details.title_id;
      this.form.first_name = details.first_name;
      this.form.last_name = details.last_name;
      this.form.gender_id = details.gender_id;
      this.form.date_of_birth = details.date_of_birth;
      this.form.occupation = details.occupation;
      this.form.national_insurance = details.national_insurance;
      this.form.security_question_id = details.security_question_id;
      this.form.security_question_answer = details.security_question_answer;
      this.form.company_name = details.company_name;
      this.form.vat_number = details.vat_number;

      if (this.form.vat_number) {
        this.form.vat_registered = true;
      }

      this.form.email_address = details.email_address;
      this.form.home_telephone = details.home_telephone;
      this.form.work_telephone = details.work_telephone;
      this.form.mobile_telephone = details.mobile_telephone;
      this.form.contact_method_id = details.contact_method_id;
      this.form.contact_time_id = details.contact_time_id;
      this.form.address_line_one = details.address_line_one;
      this.form.address_line_two = details.address_line_two;
      this.form.motoring_convictions = this.getConvictions(
        details.convictions || [],
        'motoring'
      );
      this.form.prior_claims = details.prior_claims;
      this.form.criminal_convictions = this.getConvictions(
        details.convictions || [],
        'criminal'
      );
      this.form.illnesses_disabilities = details.illnesses_disabilities;
      this.form.city = details.city;
      this.form.postcode = details.postcode;
      this.form.client_first_contact = details.client_first_contact;
      this.form.authorised_contact_required = !!details.authorised_contacts;

      if (this.form.authorised_contact_required) {
        this.form.authorised_contact_relation_to_client =
          details.authorised_contacts.relation_to_client;
        this.form.authorised_contact_name = details.authorised_contacts.name;
        this.form.authorised_contact_email = details.authorised_contacts.email;
        this.form.authorised_contact_phone_number =
          details.authorised_contacts.phone_number;
      }
    },

    getConvictions(convictions, type) {
      return convictions
        .filter(conviction => conviction.type.name === type)
        .map(conviction => ({ id: conviction.id, body: conviction.body }));
    },

    setAddress(address) {
      this.form.address_line_one = address.address_line_one;
      this.form.address_line_two = address.address_line_two;
      this.form.city = address.city;
      this.form.postcode = address.postcode;
    },

    setClientFirstContact(value) {
      this.form.client_first_contact = value;
    },

    setFirstName: debounce(function(value) {
      this.form.first_name = value;

      this.checkForDuplicateName();
    }, 400),

    setLastName: debounce(function(value) {
      this.form.last_name = value;

      this.checkForDuplicateName();
    }, 400),

    checkForDuplicateName() {
      if (this.form.first_name && this.form.last_name) {
        window.Ajax.get(
          `/incidents?filter[terms]=${this.form.first_name} ${this.form.last_name}`
        )
          .then(response => {
            if (response.data?.length > 0) {
              this.duplicateNameIncidents = response.data
                .filter(incident => incident.id != this.client.incident_id)
                .map(incident => ({
                  reference: incident.source?.claim_reference,
                  url: this.incidentUrl(incident),
                  fullName: incident.client?.full_name,
                  createdAt: incident.created_at
                }));
            } else {
              this.duplicateNameIncidents = [];
            }
          })
          .catch(err => {});
      }
    },

    clearDuplicateNameIncidents() {
      this.duplicateNameIncidents = [];
    },

    /**
     * If the user tries to change tab when there are unsaved changes in the form we
     * want to display a popup warning them they have unsaved changes.
     *
     * To do that we add a form @change event listener to the form and then emit an
     * 'unsaved-data' event whenever a form value changes. This is then picked up
     * in the parent component and that handles displaying the warning if the user
     * tries to change tabs.
     */
    changeHandler() {
      if (!this.data_entered) {
        this.data_entered = true;
      }

      this.$emit('unsaved-data', this.data_entered);
    },

    submit() {
      if (this.readonly) return;
      if (this.client_exists) {
        this.makeRequest('put');
        this.data_entered = false;
        this.$emit('unsaved-data', this.data_entered);
        return;
      }

      this.makeRequest('post');
      this.data_entered = false;
      this.$emit('unsaved-data', this.data_entered);
    },

    makeRequest(method) {
      this.form[method](`/incidents/${this.incident}/client`)
        .then(response => {
          if (!this.client_exists) {
            this.client_exists = true;
          }

          this.$store.commit('Incident/setClient', response);

          window.toastify.success(
            'Saved',
            'The client was saved successfully!'
          );

          this.form.reset();
        })
        .catch(error => {
          window.toastify.error(
            'Error',
            "We couldn't save the client - please try again."
          );
        });
    }
  },

  watch: {
    client(client) {
      this.setClientDetails(client);
    }
  }
};
</script>
